import Axios from '../../../../utils/axios.js';
import {getUser} from '../../../../utils/commonfunction.js';
import {ROLE} from '../../../../utils/Constants.js';

export type DisplayEvent =
  | {type: 'order'; items: Array<{name: string; quantity: number}>; total: number}
  | {type: 'payment'; total: number; paid: number; remaining: number; tendered?: number}
  | {type: 'cash-complete'; tendered: number; change: number}
  | {type: 'card-complete'; total: number}
  | {type: 'idle'};

type DisplayFrame = {
  restaurantId: string;
  sessionId: string;
  sequence: number;
  line1Text: string;
  line2Text: string;
};

const REQUEST_TIMEOUT_MS = 700;
const DEBOUNCE_MS = 80;
const sessionId = `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
let sequence = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
let pending: DisplayEvent | undefined;

const money = (value: number) => `JPY ${Math.max(0, Math.round(Number(value) || 0)).toLocaleString('en-US')}`;
const clean = (value: string) => value.replace(/[\r\n\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 20);

export const formatDisplayEvent = (event: DisplayEvent): Pick<DisplayFrame, 'line1Text' | 'line2Text'> => {
  switch (event.type) {
    case 'order': {
      const count = event.items.reduce((sum, item) => sum + Math.max(0, Number(item.quantity) || 0), 0);
      const latest = event.items[event.items.length - 1]?.name || `${count} ITEM${count === 1 ? '' : 'S'}`;
      return {line1Text: clean(latest), line2Text: clean(`TOTAL ${money(event.total)}`)};
    }
    case 'payment':
      return {
        line1Text: clean(`DUE ${money(event.remaining)}`),
        line2Text: clean(event.tendered ? `TENDER ${money(event.tendered)}` : `PAID ${money(event.paid)}`),
      };
    case 'cash-complete':
      return {line1Text: clean(`TENDER ${money(event.tendered)}`), line2Text: clean(`CHANGE ${money(event.change)}`)};
    case 'card-complete':
      return {line1Text: 'CARD APPROVED', line2Text: clean(`TOTAL ${money(event.total)}`)};
    case 'idle':
      return {line1Text: 'WELCOME', line2Text: 'THANK YOU'};
  }
};

const restaurantId = (): string | null => {
  const user = getUser(ROLE.CASHIER) as {_id?: string; id?: string} | null;
  return user?._id || user?.id || null;
};

const send = async (event: DisplayEvent): Promise<void> => {
  const id = restaurantId();
  if (!id) return;
  const frame = formatDisplayEvent(event);
  const payload: DisplayFrame = {...frame, restaurantId: id, sessionId, sequence: ++sequence};
  try {
    await Axios({role: ROLE.CASHIER, extraHeaders: {'Content-Type': 'application/json'}, suppressUnauthorizedHandling: true} as any)
      .post('/cashier/customer-display/stream', payload, {timeout: REQUEST_TIMEOUT_MS});
  } catch {
    // Customer-display delivery is optional and must never affect checkout.
  }
};

export const publishDisplayEvent = (event: DisplayEvent, immediate = false): void => {
  pending = event;
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    const next = pending;
    pending = undefined;
    timer = undefined;
    if (next) void send(next);
  }, immediate ? 0 : DEBOUNCE_MS);
};
